import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Check, AlertCircle, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TermsModal = ({ onAccept }) => {
  const [isAdult, setIsAdult] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasReadTerms, setHasReadTerms] = useState(false);
  const scrollRef = useRef(null);

  const handleScroll = (e) => {
    const element = e.target;
    const scrollPercentage = (element.scrollTop + element.clientHeight) / element.scrollHeight;
    
    // User has read terms when they scroll to 90% or more
    if (scrollPercentage >= 0.9) {
      setHasReadTerms(true);
    }
  };

  const handleAccept = async () => {
    if (!isAdult || !agreedToTerms) return;
    
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('session_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['X-Session-Token'] = token;
      }
      
      await axios.post(
        `${API}/auth/accept-terms`,
        { agreed_to_terms: true, is_adult: true },
        { withCredentials: true, headers }
      );
      onAccept();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to accept terms');
    } finally {
      setLoading(false);
    }
  };

  const termsContent = `MATCHSTAKE TERMS & CONDITIONS

Effective Date: January 2026
Platform Name: MatchStake

1. ACCEPTANCE OF TERMS
By accessing or using MatchStake ("Platform"), you agree to be legally bound by these Terms & Conditions. If you do not agree, you may not use the Platform.

2. ELIGIBILITY
To use MatchStake, you must:
• Be at least 18 years old (or legal age in your jurisdiction)
• Reside in a jurisdiction where skill-based peer-to-peer wagering is permitted
• Not be prohibited by local, state, or federal law

Users are responsible for understanding their local laws.

3. NATURE OF THE PLATFORM
MatchStake:
• Is a peer-to-peer skill-based gaming platform
• Does NOT act as a gambling operator
• Does NOT wager against users
• Does NOT guarantee winnings

The platform provides escrow and facilitation services only.

4. USER ACCOUNTS
Users must:
• Provide accurate information
• Maintain confidentiality of login credentials
• Not create multiple accounts to bypass restrictions
• Not impersonate another person

MatchStake reserves the right to suspend accounts for fraud, abuse, or suspicious behavior.

5. WALLET & CREDITS SYSTEM

Credits:
• Users purchase Credits to enter matches ($1 = 100 Credits)
• Credits are non-withdrawable and used for match entry fees

Cash Wallet:
• Prize winnings are deposited to your Cash Wallet
• Cash Wallet funds are withdrawable

Escrow:
• Credits are held securely during active matches
• Credits are returned if a match is cancelled or drawn

Withdrawals:
• Subject to identity verification
• Processing times may vary
• Platform may charge withdrawal fee

6. MATCH RULES
Users agree to:
• Play the agreed game under agreed rules
• Not modify game files
• Not exploit glitches
• Not collude with opponents
• Not intentionally lose

Any evidence of cheating may result in forfeiture.

7. RESULT VERIFICATION
Users must submit:
• Clear screenshot or video proof
• Visible usernames
• Final score
• Match timestamp

MatchStake reserves the right to:
• Request additional proof
• Review disputes
• Make final determination

Platform decisions are final.

8. DISPUTES
If a dispute arises:
• Both parties must submit proof within specified time
• Failure to submit proof may result in automatic loss
• Admin review decision is binding

9. PLATFORM FEES
MatchStake may deduct:
• A service fee (8%) per match
• Payment processing fees
• Withdrawal fees

Fees are non-refundable.

10. PROHIBITED CONDUCT
Users may NOT:
• Use bots or automation
• Use hacked/modded consoles
• Harass other users
• Conduct off-platform side bets
• Launder money
• Manipulate results

Violations may result in:
• Account suspension
• Permanent ban
• Funds forfeiture
• Legal reporting

11. TAX RESPONSIBILITY
Users are responsible for:
• Reporting winnings
• Paying applicable taxes

MatchStake does not provide tax advice.

12. LIMITATION OF LIABILITY
MatchStake is not liable for:
• Console malfunctions
• Internet outages
• Game server downtime
• Payment processor errors
• Third-party platform issues

Use at your own risk.

13. NO GUARANTEE OF EARNINGS
The platform does not guarantee profit, success, or winnings. Skill determines outcomes.

14. TERMINATION
MatchStake may suspend, freeze funds, or terminate accounts at its discretion.

15. REFUNDS
• Deposits are generally non-refundable
• Escrow disputes resolved per admin ruling
• Chargebacks may result in account ban

16. INTELLECTUAL PROPERTY
All platform content, logos, and systems belong to MatchStake. Unauthorized use prohibited.

17. PRIVACY
User data handled according to Privacy Policy.

18. MODIFICATIONS
MatchStake reserves the right to modify these Terms at any time. Continued use constitutes acceptance.`;

  const canCheck = hasReadTerms;
  const canContinue = isAdult && agreedToTerms;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-card rounded-2xl max-w-2xl w-full my-4"
        data-testid="terms-modal"
      >
        <div className="p-4 sm:p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#7000FF]/20 flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-[#7000FF]" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-lg sm:text-xl">Terms & Conditions</h2>
              <p className="text-white/50 text-xs sm:text-sm">Please read and scroll to the bottom to continue</p>
            </div>
          </div>
        </div>

        {/* Scrollable Terms Content */}
        <div className="relative">
          <div 
            ref={scrollRef}
            onScroll={handleScroll}
            className="h-[35vh] sm:h-[40vh] overflow-y-auto p-4 sm:p-6"
            style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.2) transparent' }}
          >
            <pre className="whitespace-pre-wrap font-body text-xs sm:text-sm text-white/70 leading-relaxed">
              {termsContent}
            </pre>
          </div>
          
          {/* Scroll indicator - shows when user hasn't read terms yet */}
          <AnimatePresence>
            {!hasReadTerms && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-[#0A0A0B] to-transparent pointer-events-none flex items-end justify-center pb-1"
              >
                <motion.div
                  animate={{ y: [0, 5, 0] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                  className="flex items-center gap-1 text-[#00F0FF] text-xs"
                >
                  <ChevronDown className="w-4 h-4" />
                  Scroll down to read all terms
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="p-4 sm:p-6 border-t border-white/10 space-y-3 sm:space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 text-red-400">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Status indicator */}
          {!hasReadTerms && (
            <div className="flex items-center gap-2 p-2 sm:p-3 rounded-lg bg-[#FFB800]/10 text-[#FFB800]">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-xs sm:text-sm">Scroll down and read all terms before accepting</span>
            </div>
          )}

          {/* Checkboxes - only enabled after reading terms */}
          <div className="space-y-2 sm:space-y-3">
            <label className={`flex items-start gap-2 sm:gap-3 group ${canCheck ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
              <Checkbox 
                checked={isAdult}
                onCheckedChange={canCheck ? setIsAdult : undefined}
                disabled={!canCheck}
                className="mt-0.5 flex-shrink-0 data-[state=checked]:bg-[#00F0FF] data-[state=checked]:border-[#00F0FF]"
                data-testid="age-checkbox"
              />
              <span className={`text-xs sm:text-sm text-white/70 ${canCheck ? 'group-hover:text-white' : ''} transition-colors`}>
                I confirm that I am <strong className="text-white">18 years or older</strong> and legally allowed to participate in skill-based competitive gaming.
              </span>
            </label>

            <label className={`flex items-start gap-2 sm:gap-3 group ${canCheck ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
              <Checkbox 
                checked={agreedToTerms}
                onCheckedChange={canCheck ? setAgreedToTerms : undefined}
                disabled={!canCheck}
                className="mt-0.5 flex-shrink-0 data-[state=checked]:bg-[#00F0FF] data-[state=checked]:border-[#00F0FF]"
                data-testid="terms-checkbox"
              />
              <span className={`text-xs sm:text-sm text-white/70 ${canCheck ? 'group-hover:text-white' : ''} transition-colors`}>
                I have read and agree to the <strong className="text-white">Terms & Conditions</strong> and understand I am using credits to enter skill-based matches.
              </span>
            </label>
          </div>

          {/* Continue Button - only appears after both checkboxes are checked */}
          <AnimatePresence>
            {canContinue && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
              >
                <Button
                  onClick={handleAccept}
                  disabled={loading}
                  className="w-full btn-primary clip-slant py-4 sm:py-6"
                  data-testid="accept-terms-btn"
                >
                  {loading ? (
                    'Processing...'
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Continue
                    </>
                  )}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default TermsModal;
